import '@Pages/GeoMMM/Results/ModelInsights.scss';
import * as R from 'ramda';
import { Fragment, useCallback, useEffect, useMemo, useState, FC } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { CartesianGrid, Line, LineChart as LC, Tooltip, XAxis, YAxis } from 'recharts';
import { ResponseCurves } from '@blisspointmedia/bpm-types/dist/GeoMMM';
import {
  capitalizeWords,
  currencyFormatter,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { LineChart } from '@Components/Charts/LineChart';
import { renderXAxisLabel, X_AXIS_LABEL_HEIGHT } from '@Components/Charts/XAxisLabel';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { WidgetContainer } from '@Components/WidgetContainer';
import {
  decayDataFormatter,
  OUTCOME_FORMATTER_TYPE,
  saturationDataFormatter
} from '@Pages/GeoMMM/DataFormatters';
import { Size } from '@Components/designConstants';
import { getColorMap as colors, Brand100, Neutral500 } from '../../../utils/colors';
import { downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';

const MEDIAN_DOT_SIZE = 7;
const GRAY_COLOR = Neutral500;
const INACTIVE_COLOR = '#d3d3d3'; // neutral200
const CustomizedDot = (props: any) => {
  const { cx, cy, stroke, payload, saturationData } = props;

  if (
    saturationData.meanValues[payload.name].dataPoint !== payload.spend ||
    saturationData.meanValues[payload.name].mean === null
  ) {
    return null;
  }

  return (
    <svg>
      <polygon
        points={`${cx},${cy - MEDIAN_DOT_SIZE} ${cx + MEDIAN_DOT_SIZE},${cy} ${cx},${
          cy + MEDIAN_DOT_SIZE
        } ${cx - MEDIAN_DOT_SIZE},${cy}`}
        fill={stroke}
      />
    </svg>
  );
};

interface SatChartTooltipContentProps {
  active?: boolean;
  payload?: any[];
  formattedSatData: any;
  outcomeVariable: 'revenue' | 'kpiVolume';
}

const SatChartTooltipContent = ({
  active,
  payload,
  formattedSatData,
  outcomeVariable
}: SatChartTooltipContentProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const items = R.sortBy(
    (item: any) => -Math.abs(item.value),
    payload?.map(item => ({
      label: item.name as string,
      value: item.value as number,
      color: item.stroke
    })) || []
  );

  return (
    <div className='sat-chart-tooltip'>
      <div className='item-list'>
        {items.map((item, i) => {
          const { label, value, color } = item;
          if (!value && value !== 0) {
            return null;
          }
          const cpa = payload[0].payload.spend / payload[0].payload.response;

          return (
            <div className='item-row' key={i}>
              <div className='item-wrapper data-label'>
                <div />
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                  <div className='line' style={{ backgroundColor: color }} />
                  <div className='name'>{`${label}: `}</div>
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>Mean Spend:</div>
                <div className='value'>
                  {currencyFormatter.format(
                    formattedSatData.meanValues[payload[0].payload.name].mean,
                    0
                  )}
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>Spend:</div>
                <div className='value'>{currencyFormatter.format(payload[0].payload.spend, 0)}</div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>{`${
                  outcomeVariable === 'revenue' ? 'Revenue' : 'KPI Volume'
                }:`}</div>
                <div className='value'>
                  {OUTCOME_FORMATTER_TYPE[outcomeVariable].format(payload[0].payload.response, 0)}
                </div>
              </div>
              {outcomeVariable === 'revenue' && (
                <div className='item-wrapper'>
                  <div className='sat-tooltip-header-item'>ROAS:</div>
                  <div className='value'>
                    {(payload[0].payload.response / payload[0].payload.spend).toFixed(2)}
                  </div>
                </div>
              )}
              {outcomeVariable === 'kpiVolume' && (
                <div className='item-wrapper'>
                  <div className='sat-tooltip-header-item'>CPA:</div>
                  <div className='value'>{`${
                    cpa === Infinity ? 0 : currencyFormatter.format(cpa)
                  }`}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface ModelInsightsProps {
  outcomeVariable: 'revenue' | 'kpiVolume';
  responseCurvesRows: any[];
  adstockRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
  maxDimension: string;
  responseCurveDivisor: number;
}

export const ModelInsights: FC<ModelInsightsProps> = ({
  outcomeVariable,
  responseCurvesRows,
  adstockRows,
  channelBreakoutMap,
  maxDimension,
  responseCurveDivisor
}) => {
  const { cid: company } = useCompanyInfo();
  const [formattedSatData, setFormattedSatData] = useState({} as any);
  const [formattedDecayData, setFormattedDecayData] = useState(
    {} as { newD: any[]; lines: any[]; groupedLines: any[] }
  );
  const [activeLine, setActiveLine] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);

  // Used to determine if a channel has multiple platforms for labeling charts
  const channelPlatformCounts = useMemo(() => {
    const counts: Record<string, Set<string>> = {};
    Object.values(channelBreakoutMap).forEach(({ channel, platform }) => {
      if (!counts[channel]) {
        counts[channel] = new Set();
      }
      counts[channel].add(platform);
    });
    return counts;
  }, [channelBreakoutMap]);

  const responseCurvesData: ResponseCurves[] = useMemo(() => {
    const responseCurves: ResponseCurves[] = [];
    if (!R.isEmpty(responseCurvesRows) && !R.isEmpty(R.keys(channelBreakoutMap))) {
      responseCurvesRows.forEach(row => {
        const {
          channel: breakout,
          spend,
          spend_multiplier: spendMultiplier,
          ci_hi: ciUpper,
          ci_lo: ciLower,
          mean: response
        } = row;

        if (channelBreakoutMap[breakout.toLowerCase()]) {
          const { channel, platform, tactic, brand_nonbrand } =
            channelBreakoutMap[breakout.toLowerCase()];

          responseCurves.push({
            name: breakout,
            channel: channel ?? null,
            platform: platform ?? null,
            tactic: tactic ?? null,
            brand_nonbrand: brand_nonbrand ?? null,
            spend: spend / responseCurveDivisor,
            spendMultiplier,
            ciLower: ciLower / responseCurveDivisor,
            ciUpper: ciUpper / responseCurveDivisor,
            response: response / responseCurveDivisor
          });
        }
      });
    }
    return responseCurves;
  }, [responseCurvesRows, channelBreakoutMap, responseCurveDivisor]);

  const adstockData: any[] = useMemo(() => {
    const adstockEntries: any[] = [];
    if (!R.isEmpty(adstockRows) && !R.isEmpty(R.keys(channelBreakoutMap))) {
      adstockRows.forEach(row => {
        const {
          channel: breakout,
          time_units: period,
          ci_hi: ciHi,
          ci_lo: ciLo,
          mean: spendCarryover
        } = row;

        if (channelBreakoutMap[breakout.toLowerCase()]) {
          const { channel, platform, tactic, brand_nonbrand } =
            channelBreakoutMap[breakout.toLowerCase()];
          adstockEntries.push({
            name: breakout,
            channel: channel ?? null,
            platform: platform ?? null,
            tactic: tactic ?? null,
            brand_nonbrand: brand_nonbrand ?? null,
            spendCarryover,
            period,
            ciHi,
            ciLo
          });
        }
      });
    }
    return adstockEntries;
  }, [adstockRows, channelBreakoutMap]);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + X_AXIS_LABEL_HEIGHT + 5,
    yAxisWidthModifier: x => x + 8
  });

  useEffect(() => {
    if (responseCurvesData && !R.isEmpty(responseCurvesData)) {
      setFormattedSatData(saturationDataFormatter(responseCurvesData, channelPlatformCounts));
    }
  }, [responseCurvesData, channelPlatformCounts]);

  useEffect(() => {
    if (adstockData && !R.isEmpty(adstockData)) {
      const { newD, lines, groupedLines } = decayDataFormatter(
        adstockData,
        'spendCarryover',
        channelPlatformCounts
      );
      setFormattedDecayData({ newD, lines, groupedLines });
    }
  }, [adstockData, channelPlatformCounts]);

  const colorMap = useMemo(() => {
    if (R.isEmpty(formattedSatData)) {
      return undefined;
    }
    return colors(formattedSatData.newD);
  }, [formattedSatData]);

  const handleLegendClick = useCallback(
    (line, group?) => {
      if (activeLine === line.name) {
        setActiveLine(null);
        setActiveGroup(null);
      } else {
        setActiveLine(line.name);
        setActiveGroup(group.channel);
      }
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    line => {
      setHoveredLegend(hoveredLegend === line.name ? null : line.name);
    },
    [hoveredLegend]
  );

  const tooltip = (
    <Tooltip
      content={
        <SatChartTooltipContent
          formattedSatData={formattedSatData}
          outcomeVariable={outcomeVariable}
        />
      }
      isAnimationActive={false}
    />
  );

  const buildLegendByGroupedArea = useCallback(
    groupedAreas =>
      groupedAreas.map((group, i) => {
        const channelArea = group.areas[0];
        const areaName = channelArea.name;
        const isActiveArea = activeLine === null ? true : activeLine === areaName;
        const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === areaName;
        const activeColor = isActiveArea ? colorMap[areaName] : INACTIVE_COLOR;
        const hoverColor = isHoveredLegend ? colorMap[areaName] : INACTIVE_COLOR;
        return (
          <div
            className='legend-item'
            key={`satDecayLegenditem${i}`}
            onClick={
              maxDimension === 'Channel' ? () => handleLegendClick(channelArea, group) : undefined
            }
            onMouseEnter={
              maxDimension === 'Channel' ? () => handleLegendEnter(channelArea) : undefined
            }
            onMouseLeave={maxDimension === 'Channel' ? () => setHoveredLegend(null) : undefined}
          >
            <div className='legend-value'>
              <div>
                <div
                  className={`legend-header-wrapper${
                    maxDimension === 'Channel' ? ' channel-legend-header-wrapper' : ''
                  }`}
                >
                  {maxDimension === 'Channel' && (
                    <div
                      className='channel-line'
                      style={{
                        backgroundColor: `${activeLine ? activeColor : hoverColor}`
                      }}
                    />
                  )}
                  <div
                    className={`legend-header ${
                      activeGroup === group.channel || activeGroup === null ? '' : 'inactive-legend'
                    }`}
                  >
                    {group.channel}
                  </div>
                </div>
                {maxDimension !== 'Channel' &&
                  group.areas.map((area, j) => {
                    const areaIsActive = activeLine === null ? true : activeLine === area.name;
                    const areaIsHovered =
                      hoveredLegend === null ? true : hoveredLegend === area.name;
                    const areaActiveColor = areaIsActive ? colorMap[area.name] : INACTIVE_COLOR;
                    const areaHoverColor = areaIsHovered ? colorMap[area.name] : INACTIVE_COLOR;
                    return (
                      <InteractiveDiv
                        key={`satDecayLegendsubHeader${j}`}
                        className='sub-header-wrapper'
                        onClick={() => handleLegendClick(area, group)}
                        onMouseEnter={() => handleLegendEnter(area)}
                        onMouseLeave={() => setHoveredLegend(null)}
                      >
                        <div
                          className='line'
                          style={{
                            backgroundColor: `${activeLine ? areaActiveColor : areaHoverColor}`
                          }}
                        />
                        <div className='sub-header-text-wrap'>
                          <div className={`sub-header-1 ${areaIsActive ? '' : 'inactive-legend'}`}>
                            {area.grouping.platform}
                          </div>
                          {area.grouping.platform &&
                            (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                              <div style={{ whiteSpace: 'pre-wrap' }}> – </div>
                            )}
                          <div
                            className={`sub-header-2 ${areaIsActive ? '' : 'inactive-legend'}`}
                          >{`${
                            area.grouping.tactic ? area.grouping.tactic : ''
                          }${area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''}${
                            area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''
                          }`}</div>
                        </div>
                      </InteractiveDiv>
                    );
                  })}
              </div>
            </div>
          </div>
        );
      }),
    [
      activeLine,
      activeGroup,
      colorMap,
      handleLegendClick,
      handleLegendEnter,
      hoveredLegend,
      maxDimension
    ]
  );

  const excelDownloadSat = useCallback(() => {
    exportToExcel(responseCurvesData, `${company}_saturation`);
  }, [responseCurvesData, company]);

  const pngDownloadSat = useCallback(async () => {
    await downloadPNG('.model-insights-container .left .chart-container', `${company}_saturation`);
  }, [company]);

  const excelDownloadDecay = useCallback(() => {
    exportToExcel(adstockData, `${company}_decay`);
  }, [adstockData, company]);

  const pngDownloadDecay = useCallback(async () => {
    await downloadPNG('.model-insights-container .right .chart-container', `${company}_decay`);
  }, [company]);

  return (
    <div className='model-insights-widget'>
      <WidgetContainer
        collapsible
        header='Model Insights'
        subHeader='The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response.'
      >
        <div className='model-insights-container' style={{ display: 'flex' }}>
          <div className='left' style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <ChartContainer
              title='Saturation Curves'
              rightActions={
                <>
                  <InfoTooltip size={Size.MD}>
                    Most media tactics will exhibit some form of diminishing returns, due to
                    additional investment reaching less qualified audiences and/or additional user
                    frequency having diminished impact. The model training process estimates the
                    shape of these saturation curves on a channel by channel basis. Examining these
                    curves yields insight into the scalability of each channel and the optimality of
                    incremental investments (see above section).
                  </InfoTooltip>
                  <DownloadDropdown onClickOptions={[excelDownloadSat, pngDownloadSat]} />
                </>
              }
            >
              {!R.isEmpty(formattedSatData) && (
                <>
                  <div className='sat-wrapper'>
                    <div className='sat-chart-title'>
                      {outcomeVariable === 'revenue' ? 'Revenue' : 'KPI Volume'}
                    </div>
                    <div className='sat-chart-box'>
                      <AutoSizer
                        renderProp={({ width, height }) => (
                          <LC width={width} height={height} ref={setChartRef}>
                            <CartesianGrid
                              stroke={CARTESIAN_GRID_STROKE}
                              strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                              vertical={false}
                            />
                            <XAxis
                              axisLine={false}
                              dataKey='spend'
                              height={xAxisHeight}
                              tick={TICK_STYLE}
                              tickFormatter={val => currencyFormatter.format(val, 0)}
                              type='number'
                              label={renderXAxisLabel('Spend')}
                            />
                            <YAxis
                              axisLine={false}
                              dataKey='response'
                              tick={TICK_STYLE}
                              tickFormatter={val => {
                                const decimals = val > 1000000 ? 1 : 0;
                                return OUTCOME_FORMATTER_TYPE[outcomeVariable].format(
                                  val,
                                  decimals
                                );
                              }}
                              tickLine={false}
                              width={yAxisWidth}
                            />
                            {activeLine && tooltip}

                            {formattedSatData.newD.map(line => {
                              const isActiveLine =
                                activeLine === null ? true : activeLine === line.name;
                              const isHoveredLegend =
                                hoveredLegend === null ? true : hoveredLegend === line.name;
                              const activeColor = isActiveLine ? colorMap[line.name] : GRAY_COLOR;
                              const hoverColor = isHoveredLegend ? colorMap[line.name] : GRAY_COLOR;

                              if (!isActiveLine && activeLine !== null) {
                                return null;
                              }
                              return (
                                <Line
                                  data={line.data}
                                  dataKey='response'
                                  dot={<CustomizedDot saturationData={formattedSatData} />}
                                  isAnimationActive={false}
                                  key={line.name}
                                  name={line.name}
                                  stroke={activeLine ? activeColor : hoverColor}
                                  strokeWidth={3}
                                />
                              );
                            })}
                          </LC>
                        )}
                      />
                    </div>
                  </div>
                  <div className='right-of-chart'>
                    <div className='legend'>
                      {buildLegendByGroupedArea(
                        R.sortBy(R.compose(R.toLower, R.prop('channel')))(
                          formattedSatData.groupedLines
                        )
                      )}
                    </div>
                    <div className='mean-legend-label'>
                      <svg width='15' height='15' viewBox='0 0 15 15'>
                        <polygon points='7.5,0 15,7.5 7.5,15 0,7.5' fill={Brand100} />
                      </svg>
                      <div className='mean-text'>
                        Mean <br /> spend
                      </div>
                    </div>
                  </div>
                </>
              )}
            </ChartContainer>
          </div>
          <div className='right'>
            <ChartContainer
              rightActions={
                <>
                  <InfoTooltip size={Size.MD}>
                    Few consumers, upon being exposed to an advertisement, drop everything and
                    respond immediately; there is almost always some lag between exposure and
                    response. The model training process estimates the extent of this response lag
                    on a channel by channel basis using a technique known as Adstock. Typically
                    (though not always), we see upper-funnel tactics having longer response lags and
                    lower-funnel tactics converting more quickly. Examining these curves can help us
                    understand channel properties and set appropriate expectations for channel
                    investments.
                  </InfoTooltip>
                  <DownloadDropdown onClickOptions={[excelDownloadDecay, pngDownloadDecay]} />
                </>
              }
              title='Spend Decay Curves'
            >
              <div className='decay-wrapper'>
                <div className='decay-chart-title'>Spend Carryover</div>
                {Object.keys(formattedDecayData).length && (
                  <LineChart
                    customTooltipHeader={val => `Week ${val} Spend Carryover`}
                    data={formattedDecayData?.newD}
                    lineDataKeys={formattedDecayData?.lines}
                    groupedAreas={R.sortBy(R.compose(R.toLower, R.prop('channel')))(
                      formattedDecayData?.groupedLines
                    )}
                    tooltipLabelFormatter={val => capitalizeWords(val, false, true)}
                    tooltipFormatter={val => (val ? `${numberFormatter.format(val, 0)}%` : val)}
                    usePercentageYAxis
                    xAxisDataKey='date'
                    xAxisTickFormatter={val => `Week ${val}`}
                    maxChannelDimension={maxDimension}
                    tooltipShape='line'
                  />
                )}
              </div>
            </ChartContainer>
          </div>
        </div>
      </WidgetContainer>
    </div>
  );
};
